import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Send, Bot, User, Hash, Calculator, Calendar, Star, Clock, Sparkles, Zap, Heart, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTION_CATEGORIES = [
  {
    id: 'basics',
    name: 'Les bases',
    icon: Sparkles,
    color: 'bg-emerald-100 border-emerald-300 text-emerald-700',
    questions: [
      "Qu'est-ce que la numérologie ?",
      "Comment calcule-t-on le chemin de vie ?",
      "Quelle est la différence entre numérologie et astrologie ?",
    ]
  },
  {
    id: 'numbers',
    name: 'Les nombres',
    icon: Hash,
    color: 'bg-teal-100 border-teal-300 text-teal-700',
    questions: [
      "Que signifie le nombre 7 en numérologie ?",
      "Quels sont les nombres maîtres ?",
      "Comment interpréter le nombre d'expression ?",
    ]
  },
  {
    id: 'cycles',
    name: 'Cycles & Années',
    icon: Calendar,
    color: 'bg-cyan-100 border-cyan-300 text-cyan-700',
    questions: [
      "Comment calculer mon année personnelle ?",
      "Que signifie l'année personnelle 1 ?",
      "Combien dure un cycle en numérologie ?",
    ]
  },
  {
    id: 'karma',
    name: 'Karma',
    icon: Zap,
    color: 'bg-rose-100 border-rose-300 text-rose-700',
    questions: [
      "Quelles sont les dettes karmiques ?",
      "Que signifie la dette karmique 16 ?",
      "Comment identifier mes nombres karmiques ?",
    ]
  },
  {
    id: 'mirror',
    name: 'Heures miroir',
    icon: Clock,
    color: 'bg-indigo-100 border-indigo-300 text-indigo-700',
    questions: [
      "Que signifie voir 11:11 régulièrement ?",
      "Quelle est la signification de 22:22 ?",
      "Les heures miroir ont-elles un sens ?",
    ]
  },
  {
    id: 'love',
    name: 'Compatibilité',
    icon: Heart,
    color: 'bg-pink-100 border-pink-300 text-pink-700',
    questions: [
      "Comment savoir si deux nombres sont compatibles ?",
      "Quel nombre est idéal pour le 7 ?",
      "La numérologie peut-elle prédire l'amour ?",
    ]
  },
];

const NumerologiaChatPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('basics');
  const [birthData, setBirthData] = useState(null);
  const messagesEndRef = useRef(null);

  // Check for user birth data in localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('numerologia_birth_data');
    if (savedData) {
      setBirthData(JSON.parse(savedData));
    }
  }, []);

  useEffect(() => {
    const welcomeMessage = birthData 
      ? `Bonjour ${birthData.name || ''} ! Je suis Pythia, experte en numérologie. 🔮\n\nJ'ai accès à vos données de naissance pour personnaliser mes réponses :\n• Date : ${birthData.birthDate}\n• Chemin de vie : ${birthData.lifePath || 'À calculer'}\n\nPosez-moi vos questions sur la numérologie, les nombres, les cycles de vie ou les heures miroir !`
      : `Bonjour ! Je suis Pythia, experte en numérologie. 🔮\n\nCet espace est dédié à l'apprentissage de la numérologie :\n• Comprendre les nombres et leurs vibrations\n• Décoder les cycles de vie\n• Interpréter les heures miroir\n• Explorer votre karma numérologique\n\n📚 Posez vos questions pour découvrir les secrets des nombres !\n\n💡 Pour une analyse personnalisée, calculez d'abord votre thème numérologique.`;
    
    setMessages([
      {
        role: 'assistant',
        content: welcomeMessage,
        timestamp: new Date()
      },
    ]);
  }, [birthData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/numerologia/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: messageText,
          birth_data: birthData
        })
      });

      const data = await response.json();

      if (data.success) {
        const assistantMessage = {
          role: 'assistant',
          content: data.answer,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        throw new Error(data.detail || 'Erreur');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
      
      const fallbackMessage = {
        role: 'assistant',
        content: "Je suis désolée, je rencontre un problème technique. Pour obtenir une analyse personnalisée de vos nombres, je vous invite à calculer votre thème numérologique !",
        timestamp: new Date()
      };
      setMessages((prev) => [...prev, fallbackMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestedQuestion = (question) => {
    sendMessage(question);
  };

  const currentCategory = QUESTION_CATEGORIES.find(c => c.id === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50 flex flex-col">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-emerald-200 p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/numerologia')}
            className="rounded-full text-gray-600 hover:bg-emerald-50"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Pythia - Expert Numérologie</h1>
              <p className="text-xs text-emerald-600">Posez vos questions sur les nombres</p>
            </div>
          </div>
          {birthData && (
            <div className="hidden md:flex items-center gap-2 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200">
              <Star className="h-4 w-4 text-amber-500" />
              <span className="text-xs text-emerald-700">Chemin {birthData.lifePath || '?'}</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Messages + Sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-3xl mx-auto space-y-4">
            <AnimatePresence>
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.role === 'user'
                        ? 'bg-emerald-500'
                        : 'bg-gradient-to-br from-emerald-500 to-teal-600'
                    }`}
                  >
                    {message.role === 'user' ? (
                      <User className="h-4 w-4 text-white" />
                    ) : (
                      <Bot className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <div
                    className={`flex-1 max-w-[85%] ${
                      message.role === 'user' ? 'text-right' : ''
                    }`}
                  >
                    <div
                      className={`inline-block p-4 rounded-2xl ${
                        message.role === 'user'
                          ? 'bg-emerald-500 text-white'
                          : 'bg-white border border-emerald-200 text-gray-700 shadow-sm'
                      }`}
                    >
                      <div className="text-sm leading-relaxed prose prose-sm max-w-none">
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                      </div>
                    </div>
                    <p className={`text-xs mt-1 px-2 ${message.role === 'user' ? 'text-gray-400' : 'text-gray-400'}`}>
                      {message.timestamp.toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="bg-white border border-emerald-200 p-4 rounded-2xl shadow-sm">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}} />
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}} />
                    </div>
                    <span className="text-xs text-emerald-600 ml-2">Pythia réfléchit...</span>
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Sidebar - Categories & Questions */}
        <div className="hidden lg:block w-80 border-l border-emerald-200 bg-white/50 overflow-y-auto">
          {/* Category Tabs */}
          <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-emerald-100 p-3">
            <h3 className="text-sm font-semibold mb-3 text-gray-800">Catégories</h3>
            <div className="flex flex-wrap gap-1.5">
              {QUESTION_CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-2.5 py-1.5 text-xs rounded-full border transition-all ${
                    activeCategory === category.id
                      ? category.color + ' font-semibold'
                      : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Questions for Selected Category */}
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              {currentCategory && <currentCategory.icon className="h-4 w-4 text-emerald-600" />}
              <span className="text-xs font-semibold text-gray-700">{currentCategory?.name}</span>
            </div>
            <div className="space-y-2">
              {currentCategory?.questions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestedQuestion(question)}
                  disabled={isLoading}
                  className="w-full px-3 py-2.5 text-xs rounded-xl border transition-colors text-left leading-relaxed disabled:opacity-50 disabled:cursor-not-allowed bg-white border-emerald-200 text-gray-600 hover:bg-emerald-50 hover:border-emerald-300"
                >
                  {question}
                </button>
              ))}
            </div>
            
            {/* CTA */}
            <div className="mt-6 p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
              <p className="text-xs text-emerald-700 mb-3 font-medium">
                Envie d'analyser VOS nombres ?
              </p>
              <Button
                onClick={() => navigate('/numerologia/theme')}
                size="sm"
                className="w-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white text-xs"
              >
                <Calculator className="mr-2 h-3 w-3" />
                Calculer mon thème
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Categories & Questions */}
      <div className="lg:hidden border-t border-emerald-200 p-3 bg-white/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto">
          {/* Category Pills */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-2 scrollbar-hide">
            {QUESTION_CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex-shrink-0 px-3 py-1.5 text-xs rounded-full border transition-all ${
                  activeCategory === category.id
                    ? category.color + ' font-semibold'
                    : 'bg-gray-50 border-gray-200 text-gray-500'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
          {/* Questions */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {currentCategory?.questions.map((question, index) => (
              <button
                key={index}
                onClick={() => handleSuggestedQuestion(question)}
                disabled={isLoading}
                className="flex-shrink-0 px-3 py-2 text-xs bg-white hover:bg-emerald-50 disabled:opacity-50 rounded-full border border-emerald-200 transition-colors whitespace-nowrap text-gray-600"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Input */}
      <div className="border-t-2 border-emerald-200 p-4 bg-white/90 backdrop-blur-md shadow-lg">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-emerald-600">💬 Question libre</span>
          </div>
          <div className="flex gap-3">
            <input
              data-testid="chat-input"
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Posez votre question sur la numérologie..."
              className="flex-1 px-4 py-3 rounded-full border-2 border-emerald-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
              disabled={isLoading}
            />
            <Button
              data-testid="send-button"
              onClick={() => sendMessage()}
              disabled={!inputMessage.trim() || isLoading}
              className="rounded-full w-12 h-12 p-0 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500"
            >
              <Send className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NumerologiaChatPage;
